import { Link, useLocation } from "react-router-dom";
import logoShort from "../../assets/img/kono-k.svg";
import { SearchBar } from "./SearchBar";
import {
  Fragment,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Menu,
  Transition,
  MenuButton,
  MenuItems,
  MenuItem,
} from "@headlessui/react";
import {
  BookmarkIcon,
  ShoppingCartIcon,
  XMarkIcon,
  Bars3Icon,
} from "@heroicons/react/24/outline";
import { classNames } from "../../common/functions";
import { AS9120Badge } from "../AS9120Badge";
import profilePicPlaceholder from "../../assets/img/avatar-placeholder.svg";
import { CartItem, parseStoredCart } from "../../common/models/cart";
import { useNotifications, useUser } from "../../common/stateDefs";
import useFirebaseAuthentication from "../../common/hooks/useFirebaseAuthentication";
import { useMiscSettings, useSidebar } from "../../buyerStateDefs";
import {
  convertPriceValueAndUnit,
  formatPriceValueString,
} from "./price-helpers";

type TopBarProps = {
  fullScreen: boolean;
  searchQuery: string;
  logoOnClick?: () => void;
  userProfileImg: string;
};

const SUPPORT_PHONE = "+1 (310) 564-7495";
const SUPPORT_PHONE_LINK = "tel:+13105647495";
const SUPPORT_EMAIL = "support@kono.ai";
const SUPPORT_EMAIL_LINK = "mailto:support@kono.ai";

export const TopBar = ({
  fullScreen,
  searchQuery,
  logoOnClick,
  userProfileImg,
}: TopBarProps) => {
  const user = useUser((state) => state.user);
  // Signed-in state is driven by the Firebase auth session, not the Firestore
  // profile doc — the profile doc can be missing or slow to load, and a signed-in
  // visitor must never be shown a "Sign in" button.
  const { authUser, authIsLoading } = useFirebaseAuthentication();
  const signedIn = !!authUser;
  const accountLabel =
    user?.displayName || user?.email || authUser?.email || "Account";
  const toggleSidebar = useSidebar((state) => state.toggleSidebar);
  const showSearchFeedback = useMiscSettings(
    (state) => state.showSearchFeedbackElements,
  );
  const setShowSearchFeedback = useMiscSettings(
    (state) => state.setShowSearchFeedbackElements,
  );

  const toggleSearchFeedback = () => {
    setShowSearchFeedback(!showSearchFeedback);
  };

  const userMenu = [
    { name: "My Orders", href: "/orders" },
    { name: "My Searches", href: "/searches" },
    { name: "Saved Items", href: "/saved" },
    (user?.roles || []).includes("annotator") && {
      name: showSearchFeedback
        ? "Don't show search feedback"
        : "Show search feedback",
      href: "#",
      onClick: toggleSearchFeedback,
    },
    { name: "Sign out", href: "/signout" },
  ].filter(Boolean) as { name: string; href: string; onClick?: () => void }[];

  return (
    <div className="md:fixed md:top-0 md:left-0 w-full bg-white z-40">
      <div className="mx-auto max-w-7xl">
        <div className="w-full flex justify-between">
          <div className="flex items-start">
            <button
              onClick={toggleSidebar}
              className="pt-8 pl-2 pr-1 text-[#00062e] hover:text-[#009499] shrink-0"
              aria-label="Open menu"
            >
              <Bars3Icon className="h-7 w-7" />
            </button>
            {!fullScreen && (
              <div className="md:flex">
                <div>
                  <Link to="/" onClick={logoOnClick}>
                    <img
                      src={logoShort}
                      alt="Gaias"
                      className="w-[2em] h-[1.5em] mt-8 ml-2"
                    />
                  </Link>
                </div>
                <div className="hidden md:block md:w-[30em] md:pl-3">
                  <SearchBar full={fullScreen} initialQuery={searchQuery} />
                </div>
              </div>
            )}
          </div>
          <div className="flex items-start shrink-0">
            <div className="hidden sm:flex pt-7 pb-7 pr-3 items-center shrink-0">
              <AS9120Badge imgClassName="h-7" />
            </div>
            <div className="hidden sm:flex pt-7 pb-7 pr-3 items-start shrink-0">
              <div className="h-9 flex flex-col justify-center text-xs leading-4 text-[#00062e] text-right">
                <a
                  href={SUPPORT_PHONE_LINK}
                  className="hover:underline whitespace-nowrap"
                >
                  {SUPPORT_PHONE}
                </a>
                <a href={SUPPORT_EMAIL_LINK} className="hover:underline">
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
            {/* Same structure as the desktop block above: padding on the
                outer div, then an h-9 inner box (the icons' height) that
                centers its content, so the labels line up with the icons. */}
            <div className="sm:hidden pt-7 pb-7 pr-2 shrink-0">
              <div className="h-9 flex items-center gap-2 text-[0.65rem] text-[#00062e] whitespace-nowrap">
                <a href={SUPPORT_PHONE_LINK} className="hover:underline">
                  Call
                </a>
                <a href={SUPPORT_EMAIL_LINK} className="hover:underline">
                  Email
                </a>
              </div>
            </div>
            <BookmarkedButton />
            <ShoppingCartButton />
            <div className="pt-7 pb-1 pr-3 pb-7">
              {!signedIn && !authIsLoading && (
                <div className="flex">
                  <Link
                    className="rounded-md border-2 border-[#00062e] hover:bg-slate-100 active:bg-slate-100 text-[#00062e] font-bold py-1 px-3"
                    to="/signin"
                  >
                    Sign in
                  </Link>
                </div>
              )}
              {signedIn && (
                <CornerMenu
                  menuItems={userMenu}
                  menuFace={
                    <img
                      className="h-5 w-5 m-1 rounded-full"
                      src={userProfileImg}
                      alt=""
                    />
                  }
                  subTitle={accountLabel}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CornerMenu = ({
  menuItems,
  menuFace,
  subTitle,
  bordered = true,
  className = "",
}: {
  menuItems: { name: string; href: string; onClick?: () => void }[];
  menuFace: ReactNode;
  subTitle?: string;
  bordered?: boolean;
  className?: string;
}) => {
  return (
    <Menu as="div" className={classNames("relative ml-3", className)}>
      <div className="flex items-center flex-col">
        <MenuButton
          className={classNames(
            "relative flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2",
            bordered && "border-2 border-[#00062e]",
          )}
        >
          <span className="absolute -inset-1.5" />
          <span className="sr-only">Open user menu</span>
          {menuFace}
        </MenuButton>
        {subTitle && <div className="mt-1 text-[0.5rem]">{subTitle}</div>}
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {menuItems.map((item) => (
            <MenuItem key={item.name}>
              {({ active }) => (
                <Link
                  to={item.href}
                  className={classNames(
                    active ? "bg-gray-100" : "",
                    "block px-4 py-2 text-sm text-gray-700",
                  )}
                  onClick={(e) => {
                    if (!item.href || item.href === "#") {
                      e.preventDefault();
                    }
                    if (item.onClick) {
                      item.onClick();
                    }
                  }}
                >
                  {item.name}
                </Link>
              )}
            </MenuItem>
          ))}
        </MenuItems>
      </Transition>
    </Menu>
  );
};

const CartAddedPopup = ({
  cartItems,
  onClose,
}: {
  cartItems: CartItem[];
  onClose: () => void;
}) => {
  const currency =
    cartItems.find((i) => i.product.price?.currency)?.product.price?.currency ||
    "USD";

  let total = 0;
  let hasUnpricedItems = false;

  for (const item of cartItems) {
    if (
      item.product.price?.value &&
      item.quantityRequired?.value &&
      item.quantityRequired?.unit
    ) {
      const pricePerUnit = convertPriceValueAndUnit(
        item.product,
        item.quantityRequired.unit,
      );
      if (pricePerUnit) {
        total += pricePerUnit.value * item.quantityRequired.value;
      } else {
        hasUnpricedItems = true;
      }
    } else {
      hasUnpricedItems = true;
    }
  }

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className="fixed inset-0 z-40 sm:hidden"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={classNames(
          "fixed z-50 bg-white rounded-xl shadow-xl border border-gray-200 w-80",
          // Desktop: top-right below the fixed topbar
          "sm:top-20 sm:right-4 sm:left-auto sm:translate-x-0 sm:translate-y-0",
          // Mobile: centered
          "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
        )}
      >
        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100">
          <h3 className="font-bold text-[#00062e]">
            Cart ({cartItems.length} {cartItems.length === 1 ? "item" : "items"}
            )
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <ul className="px-4 pt-3 pb-2 max-h-52 overflow-y-auto space-y-2">
          {cartItems.map((item, i) => (
            <li key={i} className="flex justify-between text-sm gap-2">
              <span className="text-gray-800 truncate">
                {(() => {
                  if (item.product.summary) {
                    return item.product.summary;
                  } else if (
                    item.product.material?.dbMaterials?.[0] &&
                    item.product.shape
                  ) {
                    return `${item.product.material.dbMaterials[0]} ${item.product.shape}`;
                  } else {
                    return item.product.id;
                  }
                })()}
              </span>
              <span className="text-gray-500 shrink-0">
                {item.quantityRequired
                  ? `${item.quantityRequired.value} ${item.quantityRequired.unit}`
                  : (item.quantityRequiredString ?? "")}
              </span>
            </li>
          ))}
        </ul>

        <div className="px-4 pb-1 text-sm text-gray-600">
          <span>Total (excl. shipping): </span>
          <span className="font-semibold text-gray-900">
            {total > 0
              ? formatPriceValueString({ value: total, currency }) +
                (hasUnpricedItems ? "+" : "")
              : "Price upon request"}
          </span>
        </div>

        <div className="p-4 pt-2">
          <Link
            to="/cart"
            onClick={onClose}
            className="rounded-full bg-[#00062e] hover:bg-slate-900 text-white font-bold block w-full py-2 text-center text-sm"
          >
            Go to Cart
          </Link>
        </div>
      </div>
    </>
  );
};

const ShoppingCartButton = () => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  const notif = useNotifications(
    (state) => state.shoppingCartUpdateNotificationCount,
  );
  const clearNotif = useNotifications(
    (state) => state.clearShoppingCartUpdateNotificationCount,
  );

  useEffect(() => {
    const storedCart = parseStoredCart(localStorage.getItem("shoppingCart"));
    setCartItems(storedCart.cartItems);
    if (notif > 0 && location.pathname === "/cart") {
      setShowPopup(false);
      clearNotif();
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      return;
    }
    if (notif > 0 && storedCart.cartItems.length > 0) {
      setShowPopup(true);
      clearNotif();
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = setTimeout(() => setShowPopup(false), 20000);
    }
  }, [clearNotif, location.pathname, notif]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  const handleClose = () => {
    setShowPopup(false);
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
  };

  return (
    <>
      <Link to="/cart">
        <div className="pt-7 pr-4 relative">
          <ShoppingCartIcon className="w-9 h-9 border-2 border-[#00062e] p-1 rounded-full text-[#00062e]" />
          {cartItems.length > 0 && (
            <div className="absolute bottom-0 right-3 bg-slate-900 text-white text-[0.2em] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {cartItems.length}
            </div>
          )}
        </div>
      </Link>
      {showPopup && (
        <CartAddedPopup cartItems={cartItems} onClose={handleClose} />
      )}
    </>
  );
};

const BookmarkedButton = () => {
  return (
    <Link to="/bookmarked">
      <div className="pt-7 pr-4 relative">
        <BookmarkIcon className="w-9 h-9 border-2 border-[#00062e] p-1 rounded-full text-[#00062e]" />
      </div>
    </Link>
  );
};

export const TopBarWrapper = (props: PropsWithChildren) => {
  const [searchQuery, setSearchQuery] = useState("");

  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const q = searchParams.get("q") || "";
    console.log("Search", q);
    setSearchQuery(q);
  }, [location.search]);

  return (
    <div>
      <TopBar
        fullScreen={false}
        searchQuery={searchQuery}
        userProfileImg={profilePicPlaceholder}
      />
      <div className="pt-16 sm:pt-24">
        <main className="mx-auto max-w-7xl px-4">{props.children}</main>
      </div>
    </div>
  );
};
